import React, { useState } from 'react';
import {
  ProductModelItem,
  ProductModelTopic,
  RelatedProductModelItem,
} from '../../../Related.types';
import { Button } from '@components/Button';
import { Modal } from '@components/Modal';
import { TechnicalDetailsTopics } from './components/TechnicalDetailsTopics';
import { ProductSelector } from './components/ProductSelector';
import { useFetchContactUsFormQuery } from '@pages/ContactUs/ContactUs.service';
import { QuoteItem } from '@components/QuoteItem';
import { quoteClickHelper } from '@helpers/quoteClickHelper';
import { getIdFromContent } from './TechnicalDetailsContainer.utils';
import './TechnicalDetailsContainer.scss';

interface TechnicalDetailsContainerProps {
  products: ProductModelItem[];
  productLine: RelatedProductModelItem;
  defaultSelectedItem: ProductModelItem;
  i18n: Record<string, string>;
  handleClose: () => void;
}

export const TechnicalDetailsContainer = ({
  products,
  productLine,
  defaultSelectedItem,
  handleClose,
  i18n,
}: TechnicalDetailsContainerProps) => {
  const contentRef = React.useRef<HTMLDivElement>(null);
  const [selectedItem, setSelectedItem] = useState<ProductModelItem>(defaultSelectedItem);
  const [quoteModalOpen, setQuoteModalOpen] = useState<boolean>(false);
  const [selectedQuoteItem, setSelectedQuoteItem] = useState<ProductModelItem>(null);

  const { data: contactUsForm } = useFetchContactUsFormQuery();
  const requestForQuoteText = contactUsForm?.I18?.RequestForQuote;

  // Logic to remove page breaks. In future we can have more.
  const filteredTopics = selectedItem.Topics.filter(
    (topic: ProductModelTopic) => topic.Name.toLowerCase() !== 'page break'
  );

  const onSelectProduct = (product: ProductModelItem) => {
    setSelectedItem(product);
  };

  const onSelectQuoteItem = (product: ProductModelItem) => {
    setSelectedQuoteItem(product);
  };

  const openQuoteModal = () => {
    setQuoteModalOpen(true);
  };

  const closeQuoteModal = () => {
    setQuoteModalOpen(false);
    setSelectedQuoteItem(null);
  };

  const onRequestAQuote = () => {
    quoteClickHelper(contactUsForm, () => {
      window.history.scrollRestoration = 'manual';
      const isProductLine = selectedQuoteItem.ProductModelId === window.pageId;
      const queryParameter = isProductLine ? '?productLine=' : '?productModel=';
      const productLinePageId = !isProductLine ? '&productLinePageId=' + window.pageId : '';

      window.history.pushState(
        {
          product: {
            isProductLine,
            name: selectedQuoteItem.ProductModelDisplayName,
            id: selectedQuoteItem.ProductModelId,
            imageSrc: selectedQuoteItem.Images[0]?.Thumbnail,
            description: selectedQuoteItem.ProductDescription,
          },
        },
        '',

        contactUsForm.I18.EnquiryTypeLink +
          queryParameter +
          selectedQuoteItem.ProductModelId +
          productLinePageId
      );

      window.history.go();
    });
  };

  return (
    <>
      <Modal
        header={
          <div className='modal-header-content'>
            <ProductSelector
              products={products}
              selectedProduct={selectedItem.ProductModelDisplayName}
              onSelectProduct={onSelectProduct}
            />
            <Button
              size='small'
              color='primary'
              text={requestForQuoteText}
              onClick={openQuoteModal}
            />
          </div>
        }
        subHeader={<TechnicalDetailsTopics topics={filteredTopics} contentRef={contentRef} />}
        onClose={handleClose}
        className='technical-details-modal'
        isOuterModal={true}
      >
        <div ref={contentRef} className='technical-details-content'>
          {filteredTopics.map((topic: ProductModelTopic, index: number) => (
            <div
              id={getIdFromContent(topic.Content, index)}
              key={index}
              className={`content-topic ${topic.Name}`}
              dangerouslySetInnerHTML={{ __html: topic.Content }}
            />
          ))}
        </div>
      </Modal>

      {quoteModalOpen && (
        <Modal
          header={
            <div className='modal-header-content'>
              <h3 className='m-0 font-light-xx-lg-lh-150'>{i18n.RequestAQuoteTitle}</h3>
            </div>
          }
          onClose={closeQuoteModal}
          className='quote-modal'
        >
          <div className='quote-items'>
            {productLine && (
              <QuoteItem
                key={productLine.ProductId}
                itemImage={productLine.ProductImageUrl}
                itemName={productLine.ProductTitle}
                buttonType='radio'
                className='modal-product'
                onSelect={() =>
                  onSelectQuoteItem({
                    ProductName: productLine.ProductTitle,
                    ProductModelDisplayName: productLine.ProductTitle,
                    ProductModelId: window.pageId,
                    Images: [
                      {
                        ImageAltText: productLine.ProductTitle,
                        Thumbnail: productLine.ProductImageUrl,
                        IsMain: true,
                        Order: 0,
                        WebpLarge: productLine.ProductImageUrl,
                        WebpMedium: productLine.ProductImageUrl,
                      },
                    ],
                    Topics: [],
                    ProductDescription: productLine.ProductDescription,
                    ProductConstructionPageId: window.pageId,
                    ProductLinePageId: window.pageId,
                    LanguageCode: '',
                  })
                }
              />
            )}
            {products.map((product: ProductModelItem) => (
              <QuoteItem
                key={product.ProductModelDisplayName}
                itemImage={product.Images[0]?.Thumbnail}
                itemName={product.ProductModelDisplayName}
                buttonType='radio'
                className='modal-product'
                onSelect={() =>
                  onSelectQuoteItem({
                    ...product,
                    ProductDescription: contentRef.current?.querySelector('p')?.textContent,
                  })
                }
              />
            ))}
          </div>
          <div className='modal-footer'>
            <Button
              size='medium'
              color='primary'
              text={i18n.Next}
              onClick={onRequestAQuote}
              disabled={!selectedQuoteItem}
            />
          </div>
        </Modal>
      )}
    </>
  );
};
